import React, { useState, useEffect } from "react";
import { supabase } from "../supabase/config";
import { getAllEvents } from "../supabase/events";
import { updateUserProfile } from "../supabase/users";
import { uploadProfilePicture } from "../supabase/storage";
import { formatDateTime } from "../utils/dateTime";
import { isAdmin } from "../constants/admin";

export default function ProfilePanel({
  currentUser,
  setCurrentUser,
  profileBackground,
  setProfileBackground,
  onClose,
  navigateTo,
  handleLogOut,
  initialView = "landing",
}) {
  const [profileView, setProfileView] = useState(initialView);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [activeEventsExpanded, setActiveEventsExpanded] = useState(true);
  const [archivedEventsExpanded, setArchivedEventsExpanded] = useState(false);
  const [userEvents, setUserEvents] = useState({ active: [], archived: [] });
  const [organizerData, setOrganizerData] = useState({});
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");

  // ─── Data loading ────────────────────────────────────────────────────────────

  const loadPendingRequests = async () => {
    if (!currentUser?.uid) return;
    const supabaseClient = window.supabase || supabase;
    try {
      const { data: events, error } = await supabaseClient
        .from("events")
        .select("*")
        .eq("creator_id", currentUser.uid)
        .not("pending_requests", "is", null);
      if (error) throw error;
      const requests = [];
      for (const event of events || []) {
        const pendingUsers = event.pending_requests || [];
        for (const userId of pendingUsers) {
          const { data: userData } = await supabaseClient
            .from("users")
            .select("id, name, profile_picture, date_of_birth")
            .eq("id", userId)
            .single();
          if (userData) {
            const birthDate = new Date(userData.date_of_birth);
            const age = new Date().getFullYear() - birthDate.getFullYear();
            requests.push({
              userId: userData.id,
              userName: userData.name,
              userAvatar: userData.profile_picture,
              userAge: age,
              eventId: event.event_id,
              eventTitle: event.title,
              requestTime: "Just now",
            });
          }
        }
      }
      setPendingRequests(requests);
      const badge = document.getElementById("requestsCount");
      if (badge) {
        const hasViewed =
          sessionStorage.getItem("hasViewedRequests") === "true";
        if (requests.length > 0 && !hasViewed) {
          badge.textContent = requests.length;
          badge.style.display = "block";
        } else {
          badge.style.display = "none";
        }
      }
    } catch (error) {
      console.error("Error loading pending requests:", error);
    }
  };

  const loadUserEvents = async () => {
    if (!currentUser?.uid) return;
    const { success, events } = await getAllEvents();
    if (success) {
      const now = new Date();
      const userCreatedEvents = events.filter(
        (e) => e.creatorId === currentUser.uid,
      );
      const userJoinedEvents = events.filter(
        (e) =>
          e.participants?.includes(currentUser.uid) &&
          e.creatorId !== currentUser.uid,
      );
      const allUserEvents = [...userCreatedEvents, ...userJoinedEvents];

      const active = allUserEvents
        .filter((e) => {
          const endDate = new Date(e.endDateTime);
          const timeSinceEnd = now - endDate;
          return now < endDate || timeSinceEnd <= 86400000;
        })
        .sort((a, b) => {
          const aStartDate = new Date(a.startDateTime);
          const bStartDate = new Date(b.startDateTime);
          const aEndDate = new Date(a.endDateTime);
          const bEndDate = new Date(b.endDateTime);
          const aFinished = now >= aEndDate;
          const bFinished = now >= bEndDate;
          if (aFinished && bFinished) return bEndDate - aEndDate;
          if (aFinished && !bFinished) return 1;
          if (!aFinished && bFinished) return -1;
          const aStarted = now >= aStartDate;
          const bStarted = now >= bStartDate;
          if (aStarted && bStarted) return aEndDate - bEndDate;
          if (aStarted && !bStarted) return -1;
          if (!aStarted && bStarted) return 1;
          if (aStartDate.getTime() !== bStartDate.getTime())
            return aStartDate - bStartDate;
          return aEndDate - bEndDate;
        });

      const archived = allUserEvents.filter((e) => {
        const endDate = new Date(e.endDateTime);
        return now - endDate >= 86400000;
      });

      const uniqueCreatorIds = [...new Set(active.map((e) => e.creatorId))];
      const newOrganizerData = {};
      for (const creatorId of uniqueCreatorIds) {
        if (creatorId === currentUser?.uid) {
          newOrganizerData[creatorId] = {
            name: currentUser.profile.name,
            profilePicture: currentUser.profile.profilePicture,
          };
        } else {
          try {
            const supabaseClient = window.supabase || supabase;
            const { data, error } = await supabaseClient
              .from("users")
              .select("name, profile_picture")
              .eq("id", creatorId)
              .single();
            if (!error && data) {
              newOrganizerData[creatorId] = {
                name: data.name,
                profilePicture: data.profile_picture,
              };
            }
          } catch (err) {
            console.error("Error fetching organizer:", err);
          }
        }
      }
      setOrganizerData(newOrganizerData);
      setUserEvents({ active, archived });
    }
  };

  // ─── Effects ─────────────────────────────────────────────────────────────────

  // Expose loadUserEvents to window so external callers still work
  useEffect(() => {
    window.loadUserEvents = loadUserEvents;
    return () => {
      delete window.loadUserEvents;
    };
  }, [currentUser]);

  // Expose edit state globally so the native swipe-to-close gesture can be
  // suppressed while editing (belt-and-suspenders alongside data-editing).
  useEffect(() => {
    window._panelTextEditing = isEditingDescription;
    return () => { window._panelTextEditing = false; };
  }, [isEditingDescription]);

  // Start/stop real-time event list updater when panel mounts/unmounts
  useEffect(() => {
    loadUserEvents();
    window.startEventListUpdater(loadUserEvents);
    setTimeout(() => window.updateRequestsCount(), 100);
    return () => window.stopEventListUpdater();
  }, []);

  // Reload requests when navigating to requests view
  useEffect(() => {
    if (profileView === "requests") loadPendingRequests();
  }, [profileView]);

  // Listen for requestHandled events
  useEffect(() => {
    const handleRequestHandled = () => {
      if (profileView === "requests") loadPendingRequests();
    };
    window.addEventListener("requestHandled", handleRequestHandled);
    return () =>
      window.removeEventListener("requestHandled", handleRequestHandled);
  }, [profileView]);

  // Listen for requestsLoaded events
  useEffect(() => {
    const handleRequestsLoaded = (e) => setPendingRequests(e.detail.requests);
    window.addEventListener("requestsLoaded", handleRequestsLoaded);
    return () =>
      window.removeEventListener("requestsLoaded", handleRequestsLoaded);
  }, []);

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  const closePanel = () => {
    const panel = document.getElementById("profilePanel");
    if (panel) {
      panel.style.animation = "slideOutLeft 0.3s ease-out";
      setTimeout(() => onClose(), 300);
    } else {
      onClose();
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "transparent",
          zIndex: 9998,
        }}
        onClick={(e) => {
          e.stopPropagation();
          closePanel();
        }}
      />

      {/* Panel */}
      <div
        id="profilePanel"
        data-panel="profile"
        data-editing={isEditingDescription ? "true" : "false"}
        style={{
          position: "fixed",
          top: "79px",
          left: 0,
          width: "380px",
          maxWidth: "calc(100vw - 4px)",
          height: "calc(100vh - 79px - var(--footer-height, 50px))",
          background: "white",
          borderBottomRightRadius: "20px",
          borderTopRightRadius: "20px",
          boxShadow: "4px 0 20px rgba(0,0,0,0.08)",
          fontFamily: "'Outfit', sans-serif",
          zIndex: 9999,
          animation: "slideInLeft 0.3s ease-out",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header (hidden in events + requests views) */}
        {profileView !== "events" && profileView !== "requests" && (
          <div style={{ position: "relative", flexShrink: 0 }}>
            {/* Admin shield icon */}
            {isAdmin(currentUser?.uid) && (
              <button
                onClick={() => navigateTo("admin")}
                style={{
                  position: "absolute",
                  top: "14px",
                  right: "12px",
                  background: "rgba(255,255,255,0.9)",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 101,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.9)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#764ba2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </button>
            )}

            {/* Close/Back button */}
            <button
              onClick={() => {
                if (profileView === "landing") {
                  closePanel();
                } else {
                  setProfileView("landing");
                }
              }}
              style={{
                position: "absolute",
                top: "14px",
                left: "12px",
                background: "rgba(255,255,255,0.9)",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 101,
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "white";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.9)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              {profileView === "landing" ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#202124"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#202124"
                  strokeWidth="2.5"
                >
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
              )}
            </button>

            {/* Edit background button (profile view only) */}
            {profileView === "profile" && (
              <button
                onClick={async () => {
                  const processFile = async (file) => {
                    if (!file) return;
                    if (
                      ![
                        "image/jpeg",
                        "image/jpg",
                        "image/png",
                        "image/webp",
                      ].includes(file.type)
                    ) {
                      window.showToast(
                        "Please upload an image file (JPEG, PNG, or WebP)",
                        "error",
                      );
                      return;
                    }
                    if (file.size > 5 * 1024 * 1024) {
                      window.showToast(
                        "Image is too large. Please upload an image smaller than 5MB.",
                        "error",
                      );
                      return;
                    }
                    try {
                      const loadingDiv = document.createElement("div");
                      loadingDiv.innerHTML = `<div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:white;padding:24px;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.3);z-index:10000;text-align:center;"><p style="margin:0 0 12px 0;font-size:16px;font-weight:600;color:#202124;">Uploading background...</p><div style="width:40px;height:40px;border:3px solid #f0f0f0;border-top:3px solid #4285f4;border-radius:50%;margin:0 auto;animation:spin 1s linear infinite;"></div></div><div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:9999;"></div><style>@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}</style>`;
                      document.body.appendChild(loadingDiv);
                      const fileExt = file.name.split(".").pop();
                      const fileName = `${currentUser.uid}/background_${Date.now()}.${fileExt}`;
                      const { error: uploadError } = await supabase.storage
                        .from("background-images")
                        .upload(fileName, file, {
                          cacheControl: "3600",
                          upsert: true,
                        });
                      if (uploadError) throw uploadError;
                      const { data: urlData } = supabase.storage
                        .from("background-images")
                        .getPublicUrl(fileName);
                      const downloadURL = urlData.publicUrl;
                      await updateUserProfile(currentUser.uid, {
                        background: downloadURL,
                      });
                      setProfileBackground(downloadURL);
                      if (window.currentAppUser)
                        window.currentAppUser = {
                          ...window.currentAppUser,
                          profile: {
                            ...window.currentAppUser.profile,
                            background: downloadURL,
                          },
                        };
                      setCurrentUser({
                        ...currentUser,
                        profile: {
                          ...currentUser.profile,
                          background: downloadURL,
                        },
                      });
                      loadingDiv.remove();
                      window.showToast("Background updated!", "success");
                    } catch (error) {
                      console.error("Error uploading background:", error);
                      window.showToast(
                        "Failed to upload background: " + error.message,
                        "error",
                      );
                    }
                  };
                  // Native: use the camera/gallery picker. Web: file input.
                  if (window._isNativeApp?.()) {
                    const file = await window._pickImageFile?.({ maxSize: 1400, quality: 85 });
                    await processFile(file);
                    return;
                  }
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/*";
                  input.onchange = async (e) => { await processFile(e.target.files[0]); };
                  input.click();
                }}
                style={{
                  position: "absolute",
                  top: "14px",
                  right: "12px",
                  width: "36px",
                  height: "36px",
                  background: "rgba(255,255,255,0.9)",
                  border: "none",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  transition: "all 0.2s",
                  zIndex: 101,
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.9)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title="Upload background image"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#202124"
                  strokeWidth="2"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
            )}

            {/* Background */}
            <div
              data-profile-cover
              style={{
                height: "200px",
                background:
                  profileBackground.startsWith("http://") ||
                  profileBackground.startsWith("https://")
                    ? `url(${profileBackground})`
                    : profileBackground,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                position: "relative",
              }}
            ></div>

            {/* Profile picture */}
            <div
              style={{
                position: "relative",
                marginTop: "-80px",
                display: "flex",
                justifyContent: "center",
                marginBottom: "12px",
                zIndex: 100,
              }}
            >
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    border: "4px solid white",
                    overflow: "hidden",
                    background: "#f0f0f0",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  {currentUser?.profile?.profilePicture ? (
                    <img
                      src={currentUser.profile.profilePicture}
                      alt="Profile"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "48px",
                        color: "#5f6368",
                      }}
                    >
                      👤
                    </div>
                  )}
                </div>

                {/* Edit profile picture button (profile view only) */}
                {profileView === "profile" && (
                  <button
                    onClick={async () => {
                      const processFile = async (file) => {
                        if (!file) return;
                        if (
                          ![
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                            "image/webp",
                          ].includes(file.type)
                        ) {
                          window.showToast(
                            "Please upload an image file (JPEG, PNG, or WebP)",
                            "error",
                          );
                          return;
                        }
                        if (file.size > 2 * 1024 * 1024) {
                          window.showToast(
                            "Image is too large. Please upload an image smaller than 2MB.",
                            "error",
                          );
                          return;
                        }
                        try {
                          const loadingDiv = document.createElement("div");
                          loadingDiv.innerHTML = `<div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:white;padding:24px;border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,0.3);z-index:10000;text-align:center;"><p style="margin:0 0 12px 0;font-size:16px;font-weight:600;color:#202124;">Uploading...</p><div style="width:40px;height:40px;border:3px solid #f0f0f0;border-top:3px solid #4285f4;border-radius:50%;margin:0 auto;animation:spin 1s linear infinite;"></div></div><div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.5);z-index:9999;"></div>`;
                          document.body.appendChild(loadingDiv);
                          const {
                            success: uploadSuccess,
                            url,
                            error: uploadError,
                          } = await uploadProfilePicture(currentUser.uid, file);
                          if (!uploadSuccess)
                            throw new Error(uploadError || "Upload failed");
                          await updateUserProfile(currentUser.uid, {
                            profilePicture: url,
                          });
                          const updatedProfile = {
                            ...currentUser.profile,
                            profilePicture: url,
                          };
                          setCurrentUser({
                            ...currentUser,
                            profile: updatedProfile,
                          });
                          if (window.currentAppUser)
                            window.currentAppUser = {
                              ...window.currentAppUser,
                              profile: updatedProfile,
                            };
                          loadingDiv.remove();
                          window.showToast(
                            "Profile picture updated!",
                            "success",
                          );
                        } catch (error) {
                          console.error("Upload error:", error);
                          window.showToast(
                            "Failed to upload image: " + error.message,
                            "error",
                          );
                        }
                      };
                      if (window._isNativeApp?.()) {
                        const file = await window._pickImageFile?.({ maxSize: 1000, quality: 88 });
                        await processFile(file);
                        return;
                      }
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "image/*";
                      input.onchange = async (e) => { await processFile(e.target.files[0]); };
                      input.click();
                    }}
                    style={{
                      position: "absolute",
                      bottom: "4px",
                      right: "4px",
                      width: "32px",
                      height: "32px",
                      background: "white",
                      border: "2px solid #e0e0e0",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                      transition: "all 0.2s",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "#f8f9fa";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "white";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                    title="Change profile picture"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#202124"
                      strokeWidth="2"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Name section */}
            <div
              style={{
                background: "white",
                paddingTop: "0px",
                paddingBottom: "16px",
                paddingLeft: "24px",
                paddingRight: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderBottom: "1px solid #f0f0f0",
                flexShrink: 0,
                position: "relative",
              }}
            >
<h2
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "500",
                  color: "#202124",
                }}
              >
                {currentUser?.profile?.name || "User"}
              </h2>
            </div>
          </div>
        )}

        {/* Scrollable content */}
        <div
          style={{
            padding:
              profileView === "events" || profileView === "requests"
                ? "70px 24px 24px 24px"
                : "24px 24px 0 24px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          {/* Back button for events + requests views */}
          {(profileView === "events" || profileView === "requests") && (
            <button
              onClick={() => setProfileView("landing")}
              style={{
                position: "absolute",
                top: "14px",
                left: "12px",
                background: "rgba(255,255,255,0.9)",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 101,
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                transition: "all 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "white";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.9)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#202124"
                strokeWidth="2.5"
              >
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
          )}

          {/* ── LANDING VIEW ── */}
          {profileView === "landing" && (
            <div style={{ animation: "slideViewOut 0.3s ease-out" }}>
              <div
                data-profile-menu
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginBottom: "24px",
                  alignItems: "center",
                }}
              >
                {/* Profile button */}
                <button
                  onClick={() => setProfileView("profile")}
                  style={{
                    background: "white",
                    border: "2px solid #e0e0e0",
                    borderRadius: "12px",
                    padding: "20px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    width: "200px",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#4285f4";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#e0e0e0";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4285f4"
                    strokeWidth="2"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#202124",
                    }}
                  >
                    Profile
                  </span>
                </button>

                {/* Events button */}
                <button
                  onClick={() => {
                    setProfileView("events");
                    loadUserEvents();
                  }}
                  style={{
                    background: "white",
                    border: "2px solid #e0e0e0",
                    borderRadius: "12px",
                    padding: "20px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    width: "200px",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#34a853";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#e0e0e0";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#34a853"
                    strokeWidth="2"
                  >
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      ry="2"
                    ></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#202124",
                    }}
                  >
                    Events
                  </span>
                </button>

                {/* Requests button */}
                <button
                  onClick={() => {
                    setProfileView("requests");
                    loadPendingRequests();
                    sessionStorage.setItem(
                      "requestsPanelLastViewed",
                      Date.now().toString(),
                    );
                    const badge = document.getElementById("requestsCount");
                    if (badge) badge.style.display = "none";
                  }}
                  style={{
                    background: "white",
                    border: "2px solid #e0e0e0",
                    borderRadius: "12px",
                    padding: "20px 16px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    width: "200px",
                    position: "relative",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#ea4335";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.15)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#e0e0e0";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(0,0,0,0.1)";
                  }}
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ea4335"
                    strokeWidth="2"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="23" y1="11" x2="17" y2="11"></line>
                  </svg>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#202124",
                    }}
                  >
                    Requests
                  </span>
                  <span
                    id="requestsCount"
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      background: "#ea4335",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "12px",
                      fontWeight: "600",
                      display: "none",
                    }}
                  >
                    0
                  </span>
                </button>
              </div>
              <div style={{ flex: 1, minHeight: "20px" }}></div>
            </div>
          )}

          {/* ── PROFILE VIEW ── */}
          {profileView === "profile" && (
            <div style={{ animation: "slideViewIn 0.3s ease-out" }}>
              {/* Description */}
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "16px",
                  textAlign: "left",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#5f6368",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  Description
                </label>
                {!isEditingDescription ? (
                  <div style={{ position: "relative" }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#5f6368",
                        lineHeight: "1.5",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        paddingRight: "32px",
                      }}
                    >
                      {currentUser?.profile?.description ||
                        "No description yet"}
                    </p>
                    <button
                      onClick={() => {
                        window._panelTextEditing = true; // sync, before render
                        setEditedDescription(
                          currentUser?.profile?.description || "",
                        );
                        setIsEditingDescription(true);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0.6,
                        transition: "opacity 0.2s",
                        position: "absolute",
                        top: "0",
                        right: "-16px",
                      }}
                      onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
                      onMouseOut={(e) =>
                        (e.currentTarget.style.opacity = "0.6")
                      }
                      title="Edit description"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#5f6368"
                        strokeWidth="2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div
                    style={{
                      border: "2px solid #dadce0",
                      borderRadius: "8px",
                      padding: "12px",
                      background: "white",
                    }}
                  >
                    <textarea
                      ref={(el) => {
                        if (el) {
                          el.style.height = "auto";
                          el.style.height =
                            Math.max(100, Math.min(400, el.scrollHeight)) +
                            "px";
                        }
                      }}
                      value={editedDescription}
                      onChange={(e) => {
                        setEditedDescription(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height =
                          Math.max(100, Math.min(400, e.target.scrollHeight)) +
                          "px";
                      }}
                      maxLength={500}
                      autoFocus
                      style={{
                        width: "100%",
                        fontSize: "14px",
                        padding: "8px",
                        border: "none",
                        background: "transparent",
                        borderRadius: "6px",
                        resize: "none",
                        fontFamily: "inherit",
                        boxSizing: "border-box",
                        outline: "none",
                        minHeight: "100px",
                        maxHeight: "400px",
                        overflow: "auto",
                        overflowWrap: "break-word",
                        wordWrap: "break-word",
                      }}
                      placeholder="Add a description..."
                    />
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#5f6368",
                        fontWeight: "500",
                        textAlign: "center",
                        marginTop: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      {editedDescription.length}/500
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <button
                        onClick={async () => {
                          try {
                            if (!currentUser?.uid) {
                              window.showToast(
                                "User not found. Please refresh the page.",
                                "error",
                              );
                              return;
                            }
                            const result = await updateUserProfile(
                              currentUser.uid,
                              { description: editedDescription.trim() },
                            );
                            if (result.success) {
                              setCurrentUser({
                                ...currentUser,
                                profile: {
                                  ...currentUser.profile,
                                  description: editedDescription.trim(),
                                },
                              });
                              if (window.currentAppUser)
                                window.currentAppUser = {
                                  ...window.currentAppUser,
                                  profile: {
                                    ...window.currentAppUser.profile,
                                    description: editedDescription.trim(),
                                  },
                                };
                              window._panelTextEditing = false;
                              setIsEditingDescription(false);
                            } else {
                              window.showToast(
                                "Failed to save description: " + result.error,
                                "error",
                              );
                            }
                          } catch (error) {
                            window.showToast(
                              "Failed to save description",
                              "error",
                            );
                          }
                        }}
                        style={{
                          background: "#34a853",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          padding: "10px 28px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "600",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.background = "#2d8e47")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.background = "#34a853")
                        }
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { window._panelTextEditing = false; setIsEditingDescription(false); }}
                        style={{
                          background: "#ea4335",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          padding: "10px 28px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "600",
                          transition: "all 0.2s",
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.background = "#d33426")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.background = "#ea4335")
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Age */}
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "16px",
                  textAlign: "left",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#5f6368",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  Age
                </label>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#202124",
                    fontWeight: "500",
                  }}
                >
                  {(() => {
                    if (!currentUser?.profile?.dateOfBirth) return "Not set";
                    const birthDate = new Date(currentUser.profile.dateOfBirth);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (
                      monthDiff < 0 ||
                      (monthDiff === 0 && today.getDate() < birthDate.getDate())
                    )
                      age--;
                    return `${age} years old`;
                  })()}
                </p>
              </div>

              {/* Gender */}
              <div
                style={{
                  background: "#f8f9fa",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "24px",
                  textAlign: "left",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#5f6368",
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  Gender
                </label>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#202124",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  {(() => {
                    const gender = currentUser?.profile?.gender;
                    switch (gender) {
                      case "man":
                        return (
                          <>
                            <span>👨</span> Man
                          </>
                        );
                      case "woman":
                        return (
                          <>
                            <span>👩</span> Woman
                          </>
                        );
                      case "different":
                        return (
                          <>
                            <span>🌈</span> Different
                          </>
                        );
                      default:
                        return "Not set";
                    }
                  })()}
                </p>
              </div>
              <div style={{ flex: 1, minHeight: "20px" }}></div>
            </div>
          )}

          {/* ── EVENTS VIEW ── */}
          {profileView === "events" && (
            <div style={{ animation: "slideViewIn 0.3s ease-out" }}>
              {/* Active Events */}
              <div style={{ marginBottom: "16px" }}>
                <button
                  onClick={() => setActiveEventsExpanded(!activeEventsExpanded)}
                  style={{
                    width: "100%",
                    background: "#f8f9fa",
                    border: "none",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = "#f1f3f4")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "#f8f9fa")
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#202124",
                      }}
                    >
                      Active Events
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#5f6368",
                        background: "#e0e0e0",
                        padding: "2px 8px",
                        borderRadius: "12px",
                      }}
                    >
                      {userEvents.active.length}
                    </span>
                  </div>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5f6368"
                    strokeWidth="2"
                    style={{
                      transform: activeEventsExpanded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {activeEventsExpanded && (
                  <div style={{ marginTop: "12px" }}>
                    {userEvents.active.length === 0 ? (
                      <p
                        style={{
                          textAlign: "center",
                          color: "#5f6368",
                          fontSize: "14px",
                          padding: "20px",
                        }}
                      >
                        No active events
                      </p>
                    ) : (
                      (() => {
                        const ongoing = [],
                          upcoming = [],
                          finished = [];
                        userEvents.active.forEach((event) => {
                          const status = window.calculateEventStatus(
                            event.startDateTime,
                            event.endDateTime,
                          );
                          if (status.hasEnded) finished.push(event);
                          else if (status.hasStarted && status.endDate)
                            ongoing.push(event);
                          else upcoming.push(event);
                        });
                        ongoing.sort(
                          (a, b) =>
                            window.calculateEventStatus(
                              a.startDateTime,
                              a.endDateTime,
                            ).endDate -
                            window.calculateEventStatus(
                              b.startDateTime,
                              b.endDateTime,
                            ).endDate,
                        );
                        upcoming.sort(
                          (a, b) =>
                            window.calculateEventStatus(
                              a.startDateTime,
                              a.endDateTime,
                            ).startDate -
                            window.calculateEventStatus(
                              b.startDateTime,
                              b.endDateTime,
                            ).startDate,
                        );
                        finished.sort(
                          (a, b) =>
                            window.calculateEventStatus(
                              b.startDateTime,
                              b.endDateTime,
                            ).endDate -
                            window.calculateEventStatus(
                              a.startDateTime,
                              a.endDateTime,
                            ).endDate,
                        );
                        return [...ongoing, ...upcoming, ...finished].map(
                          (event) => {
                            const uniqueEventId =
                              `${event.placeId}_${event.startDateTime}`.replace(
                                /[^a-zA-Z0-9]/g,
                                "_",
                              );
                            const status = window.calculateEventStatus(
                              event.startDateTime,
                              event.endDateTime,
                            );
                            const { statusColor, shouldPulse } = status;
                            return (
                              <div
                                className="event-card-wrapper event-card-enter"
                                key={uniqueEventId}
                                data-event-card="true"
                                data-start-date-time={event.startDateTime}
                                data-end-date-time={event.endDateTime}
                                onClick={() => {
                                  window.openedFromList = false;
                                  window.showEventDetailsPanel(
                                    event,
                                    event.placePhotoUrl,
                                  );
                                }}
                                style={{
                                  padding: "16px",
                                  border: `2px solid ${statusColor}`,
                                  borderRadius: "12px",
                                  marginBottom: "12px",
                                  position: "relative",
                                  overflow: "visible",
                                  boxShadow:
                                    "0 8px 20px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15)",
                                  transition:
                                    "box-shadow 0.2s ease, transform 0.2s ease",
                                  cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow =
                                    "inset 0 4px 12px rgba(0,0,0,0.15), inset 0 2px 6px rgba(0,0,0,0.1)";
                                  e.currentTarget.style.transform =
                                    "scale(0.98)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow =
                                    "0 8px 20px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15)";
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              >
                                {event.creatorId === currentUser?.uid && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "-10px",
                                      right: "-10px",
                                      width: "25px",
                                      height: "25px",
                                      background: "#e1e1e1fa",
                                      border: "2px solid #00a3b7",
                                      borderRadius: "30%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      boxShadow: "2px 2px 6px rgba(0,0,0,0.4)",
                                      zIndex: 10,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        lineHeight: 1,
                                        marginTop: "-2px",
                                      }}
                                    >
                                      👑
                                    </span>
                                  </div>
                                )}
                                {shouldPulse && (
                                  <>
                                    <style>{`@keyframes eventCardPulse_${uniqueEventId}{0%{transform:scale(1);opacity:0.6}50%{transform:scale(1.02);opacity:0.3}100%{transform:scale(1.04);opacity:0}}`}</style>
                                    <div
                                      data-card-pulse="true"
                                      style={{
                                        position: "absolute",
                                        inset: "-6px",
                                        borderRadius: "14px",
                                        border: `2px solid ${statusColor}`,
                                        animation: `eventCardPulse_${uniqueEventId} 2s ease-in-out infinite`,
                                        pointerEvents: "none",
                                        zIndex: -1,
                                      }}
                                    ></div>
                                  </>
                                )}
                                <h3
                                  style={{
                                    margin: "0 0 8px 0",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    color: "#202124",
                                    wordWrap: "break-word",
                                    overflowWrap: "break-word",
                                    lineHeight: "1.3",
                                  }}
                                >
                                  {event.placeName || event.title}
                                </h3>
                                <div style={{ marginBottom: "8px" }}>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: "13px",
                                      color: "#5f6368",
                                      lineHeight: "1.4",
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                    }}
                                  >
                                    {event.title}
                                  </p>
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#5f6368",
                                    marginBottom: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                  </svg>
                                  {formatDateTime(event.startDateTime)}
                                </div>
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#5f6368",
                                    marginBottom: "8px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                    <polyline points="12 12 8 10"></polyline>
                                  </svg>
                                  {formatDateTime(event.endDateTime)}
                                </div>
                                {/* Organizer */}
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#202124",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginTop: "8px",
                                  }}
                                >
                                  {(() => {
                                    const organizer =
                                      event.creatorId === currentUser?.uid
                                        ? {
                                            name: currentUser.profile.name,
                                            profilePicture:
                                              currentUser.profile
                                                .profilePicture,
                                          }
                                        : organizerData[event.creatorId] || {
                                            name: event.createdBy,
                                            profilePicture: null,
                                          };
                                    const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(organizer.name || "User")}&background=e8eaed&color=5f6368&size=32`;
                                    return (
                                      <>
                                        <img
                                          src={
                                            organizer.profilePicture ||
                                            fallbackUrl
                                          }
                                          alt={organizer.name}
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: `2px solid ${event.creatorId === currentUser?.uid ? "rgba(0,158,177,1)" : "#e8eaed"}`,
                                          }}
                                          onError={(e) =>
                                            (e.target.src = fallbackUrl)
                                          }
                                        />
                                        <span>{organizer.name}</span>
                                      </>
                                    );
                                  })()}
                                </div>
                                {/* Status badge */}
                                {(() => {
                                  const s = window.calculateEventStatus(
                                    event.startDateTime,
                                    event.endDateTime,
                                  );
                                  return (
                                    <>
                                      {s.shouldPulse && (
                                        <style>{`@keyframes statusPulse_${event.eventId}{0%{transform:scale(1);opacity:0.6}50%{transform:scale(1.4);opacity:0.3}100%{transform:scale(1.8);opacity:0}}`}</style>
                                      )}
                                      <div
                                        style={{
                                          position: "absolute",
                                          bottom: "12px",
                                          right: "12px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: "relative",
                                            width: "14px",
                                            height: "14px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          {s.shouldPulse && (
                                            <div
                                              style={{
                                                position: "absolute",
                                                width: "14px",
                                                height: "14px",
                                                border: `3px solid ${s.statusColor}`,
                                                borderRadius: "50%",
                                                opacity: 0.4,
                                                animation: `statusPulse_${event.eventId} 2s ease-in-out infinite`,
                                              }}
                                            ></div>
                                          )}
                                          <div
                                            style={{
                                              width: "14px",
                                              height: "14px",
                                              borderRadius: "50%",
                                              background: s.statusColor,
                                              boxShadow:
                                                "0 1px 3px rgba(0,0,0,0.2)",
                                              position: "relative",
                                              zIndex: 1,
                                            }}
                                          ></div>
                                        </div>
                                        <span
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: "600",
                                            color: s.statusColor,
                                          }}
                                        >
                                          {s.statusText}
                                        </span>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            );
                          },
                        );
                      })()
                    )}
                  </div>
                )}
              </div>

              {/* Archived Events */}
              <div style={{ marginBottom: "24px" }}>
                <button
                  onClick={() =>
                    setArchivedEventsExpanded(!archivedEventsExpanded)
                  }
                  style={{
                    width: "100%",
                    background: "#f8f9fa",
                    border: "none",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "background 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.background = "#f1f3f4")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.background = "#f8f9fa")
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#202124",
                      }}
                    >
                      Archived Events
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#5f6368",
                        background: "#e0e0e0",
                        padding: "2px 8px",
                        borderRadius: "12px",
                      }}
                    >
                      {userEvents.archived.length}
                    </span>
                  </div>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#5f6368"
                    strokeWidth="2"
                    style={{
                      transform: archivedEventsExpanded
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s",
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {archivedEventsExpanded && (
                  <div style={{ marginTop: "12px" }}>
                    {userEvents.archived.length === 0 ? (
                      <p
                        style={{
                          textAlign: "center",
                          color: "#5f6368",
                          fontSize: "14px",
                          padding: "20px",
                        }}
                      >
                        No archived events
                      </p>
                    ) : (
                      userEvents.archived.map((event) => {
                        const uniqueEventId =
                          `${event.placeId}_${event.startDateTime}`.replace(
                            /[^a-zA-Z0-9]/g,
                            "_",
                          );
                        return (
                          <div
                            key={uniqueEventId}
                            data-event-card
                            data-event-id={event.eventId}
                            onClick={() => {
                              window.openedFromList = false;
                              window.showEventDetailsPanel(
                                event,
                                event.placePhotoUrl,
                              );
                            }}
                            style={{
                              padding: "16px",
                              border: "2px solid rgb(166,127,112)",
                              borderRadius: "12px",
                              marginBottom: "12px",
                              boxShadow:
                                "0 8px 20px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15)",
                              transition:
                                "box-shadow 0.2s ease, transform 0.2s ease",
                              cursor: "pointer",
                              opacity: 0.85,
                              position: "relative",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.boxShadow =
                                "inset 0 4px 12px rgba(0,0,0,0.15), inset 0 2px 6px rgba(0,0,0,0.1)";
                              e.currentTarget.style.transform = "scale(0.98)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.boxShadow =
                                "0 8px 20px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15)";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            {event.creatorId === currentUser?.uid && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "-10px",
                                  right: "-10px",
                                  width: "25px",
                                  height: "25px",
                                  background: "#e1e1e1fa",
                                  border: "2px solid #00a3b7",
                                  borderRadius: "30%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "2px 2px 6px rgba(0,0,0,0.4)",
                                  zIndex: 10,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "16px",
                                    lineHeight: 1,
                                    marginTop: "-2px",
                                  }}
                                >
                                  👑
                                </span>
                              </div>
                            )}
                            <h3
                              style={{
                                margin: "0 0 8px 0",
                                fontSize: "16px",
                                fontWeight: "600",
                                color: "#202124",
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                lineHeight: "1.3",
                              }}
                            >
                              {event.placeName || event.title}
                            </h3>
                            <div style={{ marginBottom: "8px" }}>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "13px",
                                  color: "#5f6368",
                                  lineHeight: "1.4",
                                  wordWrap: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {event.title}
                              </p>
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#5f6368",
                                marginBottom: "4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                              </svg>
                              {formatDateTime(event.startDateTime)}
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#5f6368",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                                <polyline points="12 12 8 10"></polyline>
                              </svg>
                              {formatDateTime(event.endDateTime)}
                            </div>
<div
                              style={{
                                position: "absolute",
                                bottom: "12px",
                                right: "12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span style={{ fontSize: "16px" }}>📦</span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#5f6368",
                                }}
                              >
                                Archived
                              </span>
                            </div>
                            {(() => {
                              const endDate = new Date(event.endDateTime);
                              const removeDate = new Date(endDate.getTime() + 30 * 24 * 60 * 60 * 1000);
                              const daysLeft = Math.max(1, Math.ceil((removeDate - new Date()) / (24 * 60 * 60 * 1000)));
                              return (
                                <p style={{ margin: "8px 0 0 0", fontSize: "11px", color: "#ea4335", fontWeight: 500 }}>
                                  ⏳ Auto-removed in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                                </p>
                              );
                            })()}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              <div style={{ flex: 1, minHeight: "20px" }}></div>
            </div>
          )}

          {/* ── REQUESTS VIEW ── */}
          {profileView === "requests" && (
            <div style={{ animation: "slideViewIn 0.3s ease-out" }}>
              <div style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    background: "#f8f9fa",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#202124",
                      }}
                    >
                      Pending Requests
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#5f6368",
                        background: "#e0e0e0",
                        padding: "2px 8px",
                        borderRadius: "12px",
                      }}
                    >
                      {pendingRequests.length}
                    </span>
                  </div>
                </div>

                {pendingRequests.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#5f6368",
                    }}
                  >
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#dadce0"
                      strokeWidth="1.5"
                      style={{ margin: "0 auto 16px" }}
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="20" y1="8" x2="20" y2="14"></line>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                    <p style={{ margin: 0, fontSize: "14px" }}>
                      No pending requests
                    </p>
                  </div>
                ) : (
                  pendingRequests.map((request) => {
                    const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(request.userName || "User")}&background=e8eaed&color=5f6368&size=48`;
                    return (
                      <div
                        key={request.userId}
                        style={{
                          padding: "16px",
                          border: "2px solid #e0e0e0",
                          borderRadius: "12px",
                          marginBottom: "12px",
                          background: "white",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "12px",
                          }}
                        >
                          <img
                            src={request.userAvatar || fallbackAvatar}
                            alt={request.userName}
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid #e0e0e0",
                            }}
                            onError={(e) => (e.target.src = fallbackAvatar)}
                          />
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: "15px",
                                fontWeight: "600",
                                color: "#202124",
                                marginBottom: "4px",
                              }}
                            >
                              {request.userName}
                            </div>
                            <div style={{ fontSize: "13px", color: "#5f6368" }}>
                              Age: {request.userAge}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.showUserProfilePanel(
                                request.userId,
                                "request",
                              );
                            }}
                            style={{
                              background: "none",
                              border: "2px solid #4285f4",
                              borderRadius: "50%",
                              width: "36px",
                              height: "36px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              flexShrink: 0,
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = "#e8f0fe";
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = "none";
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                            title="View Profile"
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#4285f4"
                              strokeWidth="2"
                            >
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </button>
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#5f6368",
                            marginBottom: "12px",
                            paddingTop: "12px",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          <div style={{ marginBottom: "4px" }}>
                            <strong>Event:</strong> {request.eventTitle}
                          </div>
                          <div style={{ fontSize: "12px", color: "#9e9e9e" }}>
                            {request.requestTime}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.handleJoinRequest(
                                request.eventId,
                                request.userId,
                                "accept",
                              );
                            }}
                            style={{
                              flex: 1,
                              padding: "10px 16px",
                              background: "rgb(52,168,83)",
                              color: "white",
                              border: "2px solid green",
                              borderRadius: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = "#2d8e47";
                            }}
                            onMouseOut={(e) =>
                              (e.currentTarget.style.background = "#34a853")
                            }
                          >
                            Accept
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.handleJoinRequest(
                                request.eventId,
                                request.userId,
                                "decline",
                              );
                            }}
                            style={{
                              flex: 1,
                              padding: "10px 16px",
                              background: "white",
                              color: "rgb(234,67,53)",
                              border: "2px solid rgb(234,67,53)",
                              borderRadius: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.2s",
                            }}
                            onMouseOver={(e) => {
                              e.stopPropagation();
                              e.currentTarget.style.background = "#d9d9d9";
                            }}
                            onMouseOut={(e) =>
                              (e.currentTarget.style.background = "white")
                            }
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div style={{ flex: 1, minHeight: "20px" }}></div>
            </div>
          )}
        </div>

        {/* Bottom section (landing view only) */}
        {profileView === "landing" && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "0 24px 5px",
                background: "white",
              }}
            >
              <button
                onClick={() => window.showDeleteAccountModal()}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  color: "#ea4335",
                  fontSize: "13px",
                  fontWeight: "600",
                  padding: "6px 0 6px",
                  borderRadius: "8px",
                  transition: "background 0.2s",
                }}
              >
                Delete Account
              </button>
            </div>

            <div
              style={{
                flexShrink: 0,
                background: "white",
                padding: "16px 24px 24px 24px",
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <p
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "12px",
                  color: "#5f6368",
                  textAlign: "center",
                }}
              >
                Logged as:{" "}
                <span style={{ fontWeight: "600", color: "#202124" }}>
                  {currentUser?.email}
                </span>
              </p>
              <button
                onClick={handleLogOut}
                style={{
                  width: "100%",
                  padding: "14px",
                  background:
                    "linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.3)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Logout
              </button>
            </div>
          </>
        )}

        <style>{`
          @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
          @keyframes slideOutLeft { from { transform: translateX(0); } to { transform: translateX(-100%); } }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideViewIn { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
          @keyframes slideViewOut { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
          .event-card-wrapper { transition: all 0.3s ease-out; }
          @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
          .event-card-enter { animation: slideDown 0.3s ease-out; }
        `}</style>
      </div>
    </>
  );
}
